/* Winding somebody down: what gets done, in what order, and what is left.
 *
 * Pressing Finish on a wind-down runs all of this in one go. That is a lot of
 * irreversible-feeling work behind one button, so every decision about it lives
 * here, pure, where a test can reach it: which groups can be removed and which
 * cannot (and why), what order things happen in, what a failure does to the rest,
 * and what the person is told at the end.
 *
 * THE RULES THAT MATTER
 *
 * 1. A failure never stops the rest. Each step is independent: a group that cannot
 *    be removed must not leave somebody's account enabled. Every step reports its
 *    own result, and the summary says what is still outstanding.
 *
 * 2. Sign-in is blocked FIRST. Everything after it is tidying; this is the one
 *    that closes the door, so it must not wait behind forty group removals or fail
 *    behind one that errors.
 *
 * 3. Licences are OPT-IN, and off. Removing a licence starts a thirty-day clock
 *    after which the mailbox is deleted, and nothing here can convert it to a
 *    shared mailbox first. Doing it by default would lose somebody's email for
 *    having left. The plan says so beside the tick box.
 *
 * 4. Nothing that cannot be done is hidden. A group that is dynamic, synced from
 *    on-premises, role-granting or run from Exchange is named in the summary with
 *    its reason, because "removed from everything" that was not true is worse than
 *    a list of what is left.
 */

import type { ILeaveOutcome } from '../services/graph/LeaveSyncClient';

/* ─────────────────────────── What was found ─────────────────────────── */

/** A directory object somebody is a member of, as Graph describes it. */
export interface IMembership {
  id: string;
  /** '#microsoft.graph.group', '#microsoft.graph.directoryRole', ... */
  type: string;
  name: string;
  groupTypes?: string[];
  mailEnabled?: boolean;
  securityEnabled?: boolean;
  onPremisesSyncEnabled?: boolean | null;
  isAssignableToRole?: boolean | null;
}

/** What is true of the person's account right now. */
export interface IWindDownFacts {
  /** False when Entra has no such account: deleted, or never there. A leaver whose
   *  account has already been removed is the ordinary case, and there is nothing to
   *  block or take them out of; the rest of the wind-down still has work to do. */
  accountExists?: boolean;
  memberships: IMembership[];
  signInEnabled: boolean;
  /** Licences assigned to them directly, by SKU id. Group-assigned ones are not
   *  here: they cannot be removed from the person, only by leaving the group. */
  directLicences: string[];
  groupLicenceCount: number;
}

export type GroupVerdict = 'remove' | 'hub' | 'keep';

export interface IGroupPlan {
  id: string;
  name: string;
  verdict: GroupVerdict;
  /** Why it cannot be removed here, or what it is. */
  reason: string;
}

/** Whether this directory object is something Graph lets an administrator take
 *  somebody out of, and if not, in words, why not. */
export function classifyMembership(m: IMembership, hubGroupIds: ReadonlySet<string>): IGroupPlan {
  const base = { id: m.id, name: m.name || m.id };

  if (m.type === '#microsoft.graph.directoryRole') {
    return { ...base, verdict: 'keep', reason: 'An Entra administrator role. Remove it in Entra.' };
  }
  if (m.type !== '#microsoft.graph.group') {
    return { ...base, verdict: 'keep', reason: 'Not a group (an administrative unit or similar). Remove it in Entra.' };
  }
  if (hubGroupIds.has(m.id)) {
    return { ...base, verdict: 'hub', reason: 'Hub access, taken away as its own step.' };
  }
  if ((m.groupTypes ?? []).indexOf('DynamicMembership') >= 0) {
    return { ...base, verdict: 'keep', reason: 'Membership is decided by a rule, so they leave it when they stop matching the rule.' };
  }
  if (m.onPremisesSyncEnabled) {
    return { ...base, verdict: 'keep', reason: 'Synced from on-premises Active Directory. Remove it there.' };
  }
  if (m.isAssignableToRole) {
    return { ...base, verdict: 'keep', reason: 'Grants an Entra role, which needs a Privileged Role Administrator.' };
  }
  const unified = (m.groupTypes ?? []).indexOf('Unified') >= 0;
  if (m.mailEnabled && !unified) {
    return { ...base, verdict: 'keep', reason: 'A mailing list. Those are run from Exchange, and Graph cannot change them.' };
  }
  return {
    ...base,
    verdict: 'remove',
    reason: unified ? 'Microsoft 365 group, which also takes them out of its Team and site.' : 'Security group.',
  };
}

/* ─────────────────────────── The plan ─────────────────────────── */

export interface IWindDownPlan {
  accountExists: boolean;
  signInEnabled: boolean;
  groups: IGroupPlan[];
  /** Direct licence SKU ids, with names where they are known. */
  licences: { id: string; name: string }[];
  groupLicenceCount: number;
}

export function buildPlan(
  facts: IWindDownFacts,
  hubGroupIds: ReadonlySet<string>,
  skuName: (id: string) => string,
): IWindDownPlan {
  return {
    accountExists: facts.accountExists !== false,
    signInEnabled: facts.signInEnabled,
    groups: facts.memberships.map((m) => classifyMembership(m, hubGroupIds)),
    licences: facts.directLicences.map((id) => ({ id, name: skuName(id) })),
    groupLicenceCount: facts.groupLicenceCount,
  };
}

/* ─────────────────────────── The steps ─────────────────────────── */

export type StepId = 'block' | 'sessions' | 'hub' | 'groups' | 'folder' | 'leave' | 'licences' | 'stamp';
export type StepState = 'waiting' | 'running' | 'done' | 'skipped' | 'failed';

export interface IStep {
  id: StepId;
  label: string;
  state: StepState;
  /** What happened, in words. Filled in as it runs. */
  detail: string;
  /** Sub-results worth listing, e.g. each group it could not leave. */
  items: string[];
}

export interface IWindDownOptions {
  /** Remove directly-assigned licences. Off unless somebody ticks it. */
  removeLicences: boolean;
}

/** The order matters: the door first, then the tidying, then the record. */
export function initialSteps(plan: IWindDownPlan, options: IWindDownOptions): IStep[] {
  const removable = plan.groups.filter((g) => g.verdict === 'remove').length;
  const step = (id: StepId, label: string, detail: string, state: StepState = 'waiting'): IStep => ({
    id, label, state, detail, items: [],
  });

  /* No Entra account: there is nothing to block, sign out, take out of a group or
     take a licence from. Those are skipped, with the reason where it will be read,
     and everything that is NOT about the account (folder, EZ Leave, the register)
     still runs. */
  if (!plan.accountExists) {
    const gone = 'Their Entra account no longer exists';
    return [
      step('block', 'Block their sign-in', gone, 'skipped'),
      step('sessions', 'End their sessions everywhere', gone, 'skipped'),
      step('hub', 'Take their hub access away', gone, 'skipped'),
      step('groups', 'Remove them from their groups', gone, 'skipped'),
      step('folder', 'Close their personal folder', 'Removes their access; the folder and its contents stay', 'waiting'),
      step('leave', 'Mark them a leaver in EZ Leave', 'With their last day', 'waiting'),
      step('licences', 'Remove their licences', gone, 'skipped'),
      step('stamp', 'Record the wind-down', 'Stamps the register', 'waiting'),
    ];
  }
  return [
    step('block', 'Block their sign-in', plan.signInEnabled ? 'Their account is enabled' : 'Already blocked', plan.signInEnabled ? 'waiting' : 'skipped'),
    step('sessions', 'End their sessions everywhere', 'Signs them out of every device and app', 'waiting'),
    step('hub', 'Take their hub access away', 'The hub level and every app role', 'waiting'),
    step('groups', 'Remove them from their groups', removable === 0 ? 'No other groups to remove' : `${removable} group${removable === 1 ? '' : 's'}`, removable === 0 ? 'skipped' : 'waiting'),
    step('folder', 'Close their personal folder', 'Removes their access; the folder and its contents stay', 'waiting'),
    step('leave', 'Mark them a leaver in EZ Leave', 'With their last day', 'waiting'),
    step(
      'licences',
      'Remove their licences',
      options.removeLicences
        ? `${plan.licences.length} directly assigned`
        : plan.licences.length === 0 ? 'None assigned directly' : 'Not selected: left in place',
      options.removeLicences && plan.licences.length > 0 ? 'waiting' : 'skipped',
    ),
    step('stamp', 'Record the wind-down', 'Stamps the register and keeps a note of what was removed', 'waiting'),
  ];
}

/* ─────────────────────────── Running it ─────────────────────────── */

/** Everything the runner does to the outside world. Injected, so the order and
 *  the failure handling can be tested without one. */
export interface IWindDownDeps {
  blockSignIn(): Promise<'blocked' | 'already'>;
  revokeSessions(): Promise<void>;
  revokeHubAccess(): Promise<void>;
  removeFromGroup(id: string): Promise<void>;
  closeFolder(): Promise<string>;
  markLeaver(): Promise<ILeaveOutcome>;
  removeLicences(ids: string[]): Promise<void>;
  /** Stamps the register. `note` is what was removed, for putting back. */
  stamp(note: string): Promise<void>;
}

export interface IWindDownSummary {
  steps: IStep[];
  ok: number;
  failed: number;
  /** What is still outstanding, in words. Always includes the hand-offs that
   *  Blueprint cannot do, so a clean run is never mistaken for a complete one. */
  outstanding: string[];
  /** Groups removed, for putting back. */
  removedGroups: string[];
}

const why = (e: unknown): string => (e instanceof Error ? e.message : String(e));

/** Does the lot. Never throws; every step reports itself. */
export async function runWindDown(
  plan: IWindDownPlan,
  options: IWindDownOptions,
  deps: IWindDownDeps,
  onProgress: (steps: readonly IStep[]) => void,
  today: string,
): Promise<IWindDownSummary> {
  const steps = initialSteps(plan, options);
  const removedGroups: string[] = [];
  const outstanding: string[] = [];
  const emit = (): void => onProgress(steps.map((s) => ({ ...s, items: [...s.items] })));
  const get = (id: StepId): IStep => steps.find((s) => s.id === id) as IStep;

  const run = async (id: StepId, work: (s: IStep) => Promise<void>): Promise<void> => {
    const s = get(id);
    if (s.state === 'skipped') return;
    s.state = 'running';
    emit();
    try {
      await work(s);
      if (s.state === 'running') s.state = 'done';
    } catch (e) {
      s.state = 'failed';
      s.detail = why(e);
    }
    emit();
  };

  emit();

  await run('block', async (s) => {
    const r = await deps.blockSignIn();
    s.detail = r === 'already' ? 'Already blocked' : 'Sign-in is blocked';
  });
  await run('sessions', async (s) => {
    await deps.revokeSessions();
    s.detail = 'Signed out everywhere';
  });
  await run('hub', async (s) => {
    await deps.revokeHubAccess();
    s.detail = 'No hub level, no app roles';
  });

  await run('groups', async (s) => {
    const todo = plan.groups.filter((g) => g.verdict === 'remove');
    let done = 0;
    for (const g of todo) {
      try {
        await deps.removeFromGroup(g.id);
        removedGroups.push(`${g.name} (${g.id})`);
        done += 1;
        s.detail = `${done} of ${todo.length} removed`;
        emit();
      } catch (e) {
        s.items.push(`${g.name}: ${why(e)}`);
      }
    }
    s.detail = `${done} of ${todo.length} removed`;
    if (s.items.length > 0) {
      s.state = 'failed';
      s.detail += `, ${s.items.length} could not be`;
    }
  });

  await run('folder', async (s) => {
    s.detail = await deps.closeFolder();
  });

  await run('leave', async (s) => {
    const r = await deps.markLeaver();
    s.detail = r.message;
    if (r.kind === 'failed' || r.kind === 'skipped') s.state = 'failed';
    r.problems.forEach((p) => s.items.push(p));
  });

  await run('licences', async (s) => {
    await deps.removeLicences(plan.licences.map((l) => l.id));
    s.detail = `${plan.licences.length} licence${plan.licences.length === 1 ? '' : 's'} removed. Their mailbox is deleted after 30 days unless it was converted to shared.`;
  });

  /* Stamped last, and even if something above failed: the stamp is what moves them
     out of the leaver queue, and the summary is what says what is left. It records
     what was taken so it can be put back. */
  await run('stamp', async (s) => {
    const note = removedGroups.length > 0
      ? `Wind-down ${today}: removed from groups: ${removedGroups.join('; ')}.`
      : `Wind-down ${today}.`;
    await deps.stamp(note);
    s.detail = removedGroups.length > 0 ? `Noted ${removedGroups.length} group${removedGroups.length === 1 ? '' : 's'} so they can be put back` : 'Done';
  });

  /* What is left, honestly. */
  if (!plan.accountExists) {
    outstanding.push('Their Entra account no longer exists, so nothing was blocked or removed there. Check it was deleted deliberately.');
  }
  plan.groups.filter((g) => g.verdict === 'keep').forEach((g) => outstanding.push(`${g.name}: ${g.reason}`));
  if (!options.removeLicences && plan.licences.length > 0) {
    outstanding.push(
      `Licences still assigned (${plan.licences.map((l) => l.name).join(', ')}). Convert their mailbox to shared first, then remove them, or their email is deleted after 30 days.`,
    );
  }
  if (plan.groupLicenceCount > 0) {
    outstanding.push(
      plan.groupLicenceCount === 1
        ? '1 licence comes from a group and goes when they leave the group.'
        : `${plan.groupLicenceCount} licences come from groups and go when they leave them.`,
    );
  }
  outstanding.push('Department document libraries and SharePoint site groups: finish those in Hub Admin.');
  outstanding.push('Their mailbox (shared mailbox, forwarding, manager access) is done in Exchange.');

  return {
    steps,
    ok: steps.filter((s) => s.state === 'done').length,
    failed: steps.filter((s) => s.state === 'failed').length,
    outstanding,
    removedGroups,
  };
}

/** How far through, for the bar. A step that is done, skipped or failed has been
 *  dealt with; one that is running counts for half. */
export function progressOf(steps: readonly IStep[]): number {
  if (steps.length === 0) return 0;
  const points = steps.reduce(
    (n, s) => n + (s.state === 'running' ? 0.5 : s.state === 'waiting' ? 0 : 1),
    0,
  );
  return Math.round((points / steps.length) * 100);
}
